/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictProvider = require('pict-provider');

const SPEC_SCOPE_PAGE_SIZE = 500;

// Resolved-scope persistence (localStorage). Bump the version segment when the
// entry shape changes — old entries are then simply never read again.
const SPEC_SCOPE_CACHE_PREFIX = 'HeadlightSpecScope:2:';
const SPEC_SCOPE_CACHE_TTL_SECONDS = 6 * 60 * 60;

/**
 * One plan entry at the grain every picker cascades over.
 * @typedef {{ SampleType: string, IDLineItem: number, IDMaterial: number, IDTest: number }} PlanEntry
 */

/**
 * The source a project's plan entries were resolved from.
 * @typedef {('Plan'|'Template'|'Union'|'None')} PlanEntrySource
 */

/**
 * SpecScopeResolver — shared Pict provider that resolves the plan entries
 * governing a project's material / test / line-item pickers, and caches the
 * result so downstream input providers can consult it without each picker
 * re-fetching.
 *
 * A plan entry is (IDLineItem, IDMaterial, IDTest, SampleType). Two tables hold
 * entries at exactly that grain, and the project picks between them:
 *
 *   - Plan     — ProjectLineItemMaterialTest for the project. What it WILL sample.
 *   - Template — PayItemMaterialTestRequirement for the contract's material spec
 *                set, joined to the project's LineItems on IDPayItem. What the
 *                specification REQUIRES. Used when the project has no plan rows.
 *
 * The choice is per project, never per line item: a half-planned project must
 * not mix sources, because a user could not tell which rule applied to which row.
 * `getSource()` reports which one answered.
 *
 * Enforcement is cascade narrowing, not membership. Choose a line item and the
 * material picker narrows; choose a material and the test picker narrows; sample
 * type narrows both. Consumed by the EntityFilterEngine `SamplePlanScope` filter
 * — see docs/Filters.md.
 *
 * Fail-open throughout: no source, no set, no plan, or a line item with
 * IDPayItem = 0 produces no narrowing, silently. Every getter returns null in
 * that state so consumers leave their list unfiltered.
 *
 * Online, the template comes from the platform endpoint
 * `TestSpecificationSet/PlanEntries/ForProject/:IDProject`. In the bundle path
 * the LineItem and PayItemMaterialTestRequirement rows are fetched directly and
 * joined client-side, because the bundle transport has no server to do it.
 *
 * The lab axis rides along here (labs joined to the material spec set via
 * LabTestSpecificationSetJoin). It is a different mechanism that happens to need
 * the same contract read.
 *
 * The RESOLVED scope persists in localStorage (plan entries per project,
 * material parent pairs per customer) so repeat loads skip the reads entirely —
 * only Project/Contract are re-fetched, and the cached entry is honored only
 * when it resolves to the same spec set and is inside the TTL (default 6h).
 * Configure via manifest/option `SpecScopeCache: { Enabled, TTLSeconds }`; call
 * clearScopeCache() to force a cold load. Storage failures and quota exhaustion
 * fail open to a normal full load.
 *
 * Consumers:
 *   resolver.loadForProject(IDProject)      // call once per view load
 *   resolver.getSamplePlanLineItemIDs(...)  // Set<number> | null
 *   resolver.getSamplePlanMaterialIDs(...)  // Set<number> | null
 *   resolver.getSamplePlanTestIDs(...)      // Set<number> | null
 *   resolver.getSource()                    // PlanEntrySource
 *   resolver.getAllowedLabIDs()             // Set<number> | null
 */
class SpecScopeResolver extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {Pict} */
		this.pict;
		// Customer-wide material parent map for parent/child filter expansion. Persists across
		// project loads (the tree is per-customer, not per-project) — reloads on a customer switch.
		/** @type {{ IDCustomer: number, ParentOf: Map<number, number>, ChildrenOf: Map<number, number[]> }|null} */
		this._MaterialHierarchy = null;
		this._resetScope();
	}

	_resetScope()
	{
		this._Scope =
		{
			IDProject: null,
			IDContract: null,
			MaterialSetID: 0,
			AllowedLabIDs: null,
			HasLabCoverage: false,
			SamplePlanCombos: null,
			HasSamplePlan: false,
			/** @type {PlanEntrySource} */
			Source: 'None',
			Loaded: false,
			LoadPromise: null,
		};
	}

	/**
	 * Resolve the scope for a given project. Safe to call repeatedly — returns
	 * the cached result for the same project. Caller should await this before
	 * any picker that consults the resolver renders.
	 *
	 * @param {number|string} pIDProject
	 * @return {Promise<object>} scope snapshot
	 */
	async loadForProject(pIDProject)
	{
		const IDProject = typeof pIDProject === 'number' ? pIDProject : (parseInt(String(pIDProject), 10) || 0);
		if (this._Scope.IDProject === IDProject && this._Scope.Loaded)
		{
			return this._Scope;
		}
		if (this._Scope.IDProject === IDProject && this._Scope.LoadPromise)
		{
			return this._Scope.LoadPromise;
		}
		this._resetScope();
		this._Scope.IDProject = IDProject;
		if (!(IDProject > 0))
		{
			this._Scope.Loaded = true;
			return this._Scope;
		}
		this._Scope.LoadPromise = this._doLoad(IDProject)
			.then(async (pScope) =>
			{
				// After the main resolve converges (cache / bundle / per-entity reads), floor the lab axis off the
				// resolved material spec set — one small read, independent of which path produced the scope.
				await this._loadLabScope();
				return pScope;
			})
			.catch((err) =>
			{
				this.pict?.log?.warn(`[SpecScopeResolver] Failed to resolve scope for project ${IDProject}: ${err?.message || err}`);
				return this._Scope;
			});
		return this._Scope.LoadPromise;
	}

	async _doLoad(IDProject)
	{
		const tmpNeedsSamplePlan = this._needsSamplePlan();
		const tmpCacheConfig = this._resolveCacheConfig();

		const project = await this._getJSON(`Project/${IDProject}`);
		const tmpIDCustomer = project?.IDCustomer || this.pict?.AppData?.CurrentUser?.IDCustomer || 0;
		if (project?.IDContract > 0)
		{
			const contract = await this._getJSON(`Contract/${project.IDContract}`);
			this._Scope.IDContract = contract?.IDContract || project.IDContract;
			// Two-tier resolution: direct IDTestSpecificationSet FK, else a fallback that
			// matches a set by Year. The platform endpoint applies the same rule; the two
			// must not drift.
			this._Scope.MaterialSetID = await this._resolveSetID(contract, 'MaterialIDTestSpecificationSet', 'MaterialsSpecificationYear');
		}

		if (tmpNeedsSamplePlan && !(await this._resolvePlanEntries(IDProject, tmpIDCustomer, tmpCacheConfig)))
		{
			const tmpResolved = await this._loadSamplePlan(IDProject);
			this._applyPlanEntries(tmpResolved.Combos, tmpResolved.Source);
			if (tmpCacheConfig.Enabled)
			{
				this._writeProjectScopeCache(IDProject);
			}
		}

		// Material parent map for parent/child filter expansion — loaded once per customer
		// when a material dimension is in play. The expand helpers fail open (no-op) if it
		// did not load, so the gate need not be exhaustive.
		if (this._needsMaterialHierarchy() && tmpIDCustomer > 0)
		{
			await this._loadMaterialHierarchy(tmpIDCustomer);
		}

		this._Scope.Loaded = true;
		return this._Scope;
	}

	/**
	 * Try the two cheap paths to a project's plan entries — the persisted scope cache,
	 * then one bundle execution — and report whether either answered. A bundle failure
	 * falls through to the per-entity reads, which remain the offline-proven final path.
	 *
	 * @param {number} pIDProject
	 * @param {number} pIDCustomer
	 * @param {{ Enabled: boolean, TTLSeconds: number }} pCacheConfig
	 * @return {Promise<boolean>} true when the scope now carries resolved plan entries.
	 */
	async _resolvePlanEntries(pIDProject, pIDCustomer, pCacheConfig)
	{
		const tmpCached = pCacheConfig.Enabled ? this._readProjectScopeCache(pIDProject, pCacheConfig) : null;
		if (tmpCached && tmpCached.MaterialSetID === this._Scope.MaterialSetID && tmpCached.HasSamplePlanData)
		{
			this._applySamplePlanFromCache(tmpCached);
			return true;
		}
		if (!this._resolveBundleConfig().Enabled)
		{
			return false;
		}
		const tmpBundleClient = this._getEntityBundleClient();
		if (!tmpBundleClient)
		{
			return false;
		}
		try
		{
			const tmpSteps = this._buildScopeBundleSteps(pIDProject, pIDCustomer, this._Scope.MaterialSetID);
			const tmpBundleResult = await tmpBundleClient.executeBundle(tmpSteps);
			this._applyScopeBundleData(tmpBundleResult.Data, pIDCustomer);
			if (pCacheConfig.Enabled)
			{
				this._writeProjectScopeCache(pIDProject);
			}
			return true;
		}
		catch (pError)
		{
			this.pict?.log?.warn(`[SpecScopeResolver] Bundle scope load failed (${pError?.message || pError}); falling back to entity reads.`);
			return false;
		}
	}

	/**
	 * Install a resolved entry list on the scope.
	 *
	 * @param {Array<PlanEntry>} pCombos
	 * @param {PlanEntrySource} pSource
	 */
	_applyPlanEntries(pCombos, pSource)
	{
		this._Scope.SamplePlanCombos = pCombos;
		this._Scope.Source = pSource;
		this._Scope.HasSamplePlan = pCombos.length > 0;
	}

	/**
	 * Load (once per customer) the material parent map used for parent/child filter expansion.
	 * Materials form a shallow tree via IDMaterialParent; the whole customer's
	 * IDMaterial->IDMaterialParent is cached so ancestor/descendant walks are sync + in-memory.
	 * Cached across project loads; reloads only on a customer switch. Lite read (two ids),
	 * offline-servable like the other resolver reads.
	 *
	 * @param {number} pIDCustomer
	 * @return {Promise<void>}
	 */
	async _loadMaterialHierarchy(pIDCustomer)
	{
		const tmpIDCustomer = parseInt(String(pIDCustomer), 10) || 0;
		if (!(tmpIDCustomer > 0) || (this._MaterialHierarchy && this._MaterialHierarchy.IDCustomer === tmpIDCustomer))
		{
			return;
		}
		if (this._primeMaterialHierarchyFromCache(tmpIDCustomer))
		{
			return;
		}
		const tmpCacheConfig = this._resolveCacheConfig();
		const tmpCacheKey = `${SPEC_SCOPE_CACHE_PREFIX}MaterialHierarchy:${tmpIDCustomer}`;
		const tmpRows = await this._readAllPages('Materials/Lite', `FBV~IDCustomer~EQ~${tmpIDCustomer}`);
		const tmpPairs = [];
		for (const tmpRow of tmpRows)
		{
			const tmpID = parseInt(tmpRow.IDMaterial, 10) || 0;
			if (tmpID > 0)
			{
				tmpPairs.push([ tmpID, parseInt(tmpRow.IDMaterialParent, 10) || 0 ]);
			}
		}
		this._MaterialHierarchy = this._buildMaterialHierarchy(tmpIDCustomer, tmpPairs);
		if (tmpCacheConfig.Enabled)
		{
			this._writeCacheEntry(tmpCacheKey, { TS: Date.now(), Pairs: tmpPairs });
		}
	}

	/**
	 * Build the hierarchy maps from [IDMaterial, IDMaterialParent] pairs.
	 *
	 * @param {number} pIDCustomer
	 * @param {Array<Array<number>>} pPairs
	 * @return {{ IDCustomer: number, ParentOf: Map<number, number>, ChildrenOf: Map<number, number[]> }}
	 */
	_buildMaterialHierarchy(pIDCustomer, pPairs)
	{
		const tmpParentOf = new Map();
		const tmpChildrenOf = new Map();
		for (const tmpPair of pPairs)
		{
			const tmpID = parseInt(String(tmpPair[0]), 10) || 0;
			if (!(tmpID > 0))
			{
				continue;
			}
			const tmpParent = parseInt(String(tmpPair[1]), 10) || 0;
			tmpParentOf.set(tmpID, tmpParent);
			if (tmpParent > 0)
			{
				const tmpSiblings = tmpChildrenOf.get(tmpParent);
				if (tmpSiblings)
				{
					tmpSiblings.push(tmpID);
				}
				else
				{
					tmpChildrenOf.set(tmpParent, [ tmpID ]);
				}
			}
		}
		return { IDCustomer: pIDCustomer, ParentOf: tmpParentOf, ChildrenOf: tmpChildrenOf };
	}

	/**
	 * Populate the in-memory hierarchy for a customer without any network:
	 * no-op when already resident, else from the persisted pairs cache.
	 *
	 * @param {number} pIDCustomer
	 * @return {boolean} true when the hierarchy is now resident for pIDCustomer.
	 */
	_primeMaterialHierarchyFromCache(pIDCustomer)
	{
		if (this._MaterialHierarchy && this._MaterialHierarchy.IDCustomer === pIDCustomer)
		{
			return true;
		}
		const tmpCacheConfig = this._resolveCacheConfig();
		if (!tmpCacheConfig.Enabled)
		{
			return false;
		}
		const tmpCached = this._readCacheEntry(`${SPEC_SCOPE_CACHE_PREFIX}MaterialHierarchy:${pIDCustomer}`, tmpCacheConfig.TTLSeconds);
		if (tmpCached && Array.isArray(tmpCached.Pairs))
		{
			this._MaterialHierarchy = this._buildMaterialHierarchy(pIDCustomer, tmpCached.Pairs);
			return true;
		}
		return false;
	}

	/**
	 * Resolve the resolved-scope cache configuration. Persistence is on by
	 * default with a conservative TTL — spec-set membership, sampling plans and
	 * the material tree change rarely. Override via manifest-level or provider
	 * option `SpecScopeCache: { Enabled, TTLSeconds }` (manifest wins). Tests
	 * and exotic hosts can inject a storage backend via
	 * `SpecScopeCache.Storage` (any localStorage-shaped object).
	 *
	 * @return {{ Enabled: boolean, TTLSeconds: number }}
	 */
	_resolveCacheConfig()
	{
		const tmpManifest = this.pict?.views?.PictFormMetacontroller?.manifestDescription;
		const tmpSources =
		[
			tmpManifest && tmpManifest.SpecScopeCache,
			(this.options || {}).SpecScopeCache,
		];
		const tmpResolveField = (pField, pType, pDefault) =>
		{
			for (const tmpSource of tmpSources)
			{
				if (tmpSource && typeof tmpSource[pField] === pType)
				{
					return tmpSource[pField];
				}
			}
			return pDefault;
		};
		return {
			Enabled: tmpResolveField('Enabled', 'boolean', true),
			TTLSeconds: tmpResolveField('TTLSeconds', 'number', SPEC_SCOPE_CACHE_TTL_SECONDS),
		};
	}

	/**
	 * @return {Storage|null} the persistence backend, or null when unavailable
	 * (non-browser host, sandboxed frame) — every cache path fails open on null.
	 */
	_cacheStorage()
	{
		const tmpInjected = this.options && this.options.SpecScopeCache && this.options.SpecScopeCache.Storage;
		if (tmpInjected)
		{
			return tmpInjected;
		}
		try
		{
			return (typeof localStorage !== 'undefined') ? localStorage : null;
		}
		catch (pError)
		{
			return null;
		}
	}

	/**
	 * Read one cache entry; expired or unparsable entries are deleted and
	 * treated as a miss.
	 *
	 * @param {string} pKey
	 * @param {number} pTTLSeconds
	 * @return {Record<string, any>|null}
	 */
	_readCacheEntry(pKey, pTTLSeconds)
	{
		const tmpStorage = this._cacheStorage();
		if (!tmpStorage)
		{
			return null;
		}
		try
		{
			const tmpRaw = tmpStorage.getItem(pKey);
			if (!tmpRaw)
			{
				return null;
			}
			const tmpEntry = JSON.parse(tmpRaw);
			if (!tmpEntry || typeof tmpEntry.TS !== 'number' || (Date.now() - tmpEntry.TS) > (pTTLSeconds * 1000))
			{
				tmpStorage.removeItem(pKey);
				return null;
			}
			return tmpEntry;
		}
		catch (pError)
		{
			try
			{
				tmpStorage.removeItem(pKey);
			}
			catch (pRemoveError)
			{
				// fail open
			}
			return null;
		}
	}

	/**
	 * Write one cache entry. On a quota failure, evict all spec-scope entries
	 * and retry once; a second failure is swallowed (cache is best-effort).
	 *
	 * @param {string} pKey
	 * @param {Record<string, any>} pEntry
	 */
	_writeCacheEntry(pKey, pEntry)
	{
		const tmpStorage = this._cacheStorage();
		if (!tmpStorage)
		{
			return;
		}
		const tmpSerialized = JSON.stringify(pEntry);
		try
		{
			tmpStorage.setItem(pKey, tmpSerialized);
		}
		catch (pError)
		{
			this.clearScopeCache();
			try
			{
				tmpStorage.setItem(pKey, tmpSerialized);
			}
			catch (pRetryError)
			{
				this.pict?.log?.warn(`[SpecScopeResolver] Scope cache write failed for ${pKey}: ${pRetryError?.message || pRetryError}`);
			}
		}
	}

	/**
	 * Remove every persisted spec-scope entry (all versions' project scopes and
	 * material hierarchies). Public so QA/debug tooling can force a cold load.
	 */
	clearScopeCache()
	{
		const tmpStorage = this._cacheStorage();
		if (!tmpStorage)
		{
			return;
		}
		try
		{
			const tmpKeys = [];
			for (let i = 0; i < tmpStorage.length; i++)
			{
				const tmpKey = tmpStorage.key(i);
				if (tmpKey && tmpKey.startsWith('HeadlightSpecScope:'))
				{
					tmpKeys.push(tmpKey);
				}
			}
			for (const tmpKey of tmpKeys)
			{
				tmpStorage.removeItem(tmpKey);
			}
		}
		catch (pError)
		{
			// fail open
		}
	}

	/**
	 * @param {number} pIDProject
	 * @param {{ TTLSeconds: number }} pCacheConfig
	 * @return {Record<string, any>|null} the cached project scope entry, or null.
	 */
	_readProjectScopeCache(pIDProject, pCacheConfig)
	{
		return this._readCacheEntry(`${SPEC_SCOPE_CACHE_PREFIX}Project:${pIDProject}`, pCacheConfig.TTLSeconds);
	}

	/**
	 * Persist the currently-resolved scope for a project. Plan entries are stored as
	 * [SampleType, IDLineItem, IDMaterial, IDTest] quads; HasSamplePlanData
	 * distinguishes "entries resolved (possibly empty)" from "never loaded" so a
	 * plan-scoped form can't hit an entry-less cache row.
	 *
	 * @param {number} pIDProject
	 */
	_writeProjectScopeCache(pIDProject)
	{
		const tmpCombos = this._Scope.SamplePlanCombos;
		this._writeCacheEntry(`${SPEC_SCOPE_CACHE_PREFIX}Project:${pIDProject}`,
		{
			TS: Date.now(),
			MaterialSetID: this._Scope.MaterialSetID || 0,
			Source: this._Scope.Source,
			HasSamplePlanData: Array.isArray(tmpCombos),
			SP: Array.isArray(tmpCombos) ? tmpCombos.map((pCombo) => [ pCombo.SampleType, pCombo.IDLineItem, pCombo.IDMaterial, pCombo.IDTest ]) : null,
		});
	}

	/**
	 * @param {Record<string, any>} pCachedEntry - entry with SP quads.
	 */
	_applySamplePlanFromCache(pCachedEntry)
	{
		this._applyPlanEntries(this._planEntriesFromRows(pCachedEntry.SP || [], (pQuad) => (
			{
				SampleType: pQuad[0],
				IDLineItem: pQuad[1],
				IDMaterial: pQuad[2],
				IDTest: pQuad[3],
			})), pCachedEntry.Source || 'None');
	}

	/**
	 * Resolve the bundle-execution configuration for cold loads. On by default;
	 * `SpecScopeBundle: { Enabled: false }` (manifest wins over provider
	 * options) pins the resolver to its per-entity reads.
	 *
	 * @return {{ Enabled: boolean }}
	 */
	_resolveBundleConfig()
	{
		const tmpManifest = this.pict?.views?.PictFormMetacontroller?.manifestDescription;
		const tmpSources =
		[
			tmpManifest && tmpManifest.SpecScopeBundle,
			(this.options || {}).SpecScopeBundle,
		];
		for (const tmpSource of tmpSources)
		{
			if (tmpSource && typeof tmpSource.Enabled === 'boolean')
			{
				return { Enabled: tmpSource.Enabled };
			}
		}
		return { Enabled: true };
	}

	/**
	 * @return {import('./PictProvider-EntityBundleClient.js')|null} the shared
	 * EntityBundleClient, self-registering it when the host app hasn't.
	 */
	_getEntityBundleClient()
	{
		if (this.pict?.providers?.EntityBundleClient)
		{
			return this.pict.providers.EntityBundleClient;
		}
		if (typeof this.pict?.addProvider === 'function')
		{
			this.pict.addProvider('EntityBundleClient', {}, require('./PictProvider-EntityBundleClient.js'));
			return this.pict.providers.EntityBundleClient || null;
		}
		return null;
	}

	/**
	 * Build the bundle steps equivalent to this load's per-entity reads: the project's
	 * plan rows, and the LineItem + PayItemMaterialTestRequirement rows the template
	 * fallback joins when there are none. Both are fetched unconditionally because the
	 * bundle is one round trip and the source choice happens client-side in
	 * _applyScopeBundleData. The customer Material step is omitted when the hierarchy is
	 * already resident or primeable from cache.
	 *
	 * @param {number} pIDProject
	 * @param {number} pIDCustomer
	 * @param {number} pMaterialSetID
	 * @return {Array<Record<string, any>>}
	 */
	_buildScopeBundleSteps(pIDProject, pIDCustomer, pMaterialSetID)
	{
		const tmpSteps =
		[
			{ Entity: 'ProjectLineItemMaterialTest', Filter: `FBV~IDProject~EQ~${pIDProject}`, Destination: 'Bundle.ProjectLineItemMaterialTest', AllRecords: true, Projection: { Mode: 'LiteExtended', ExtraColumns: [ 'SampleType' ] } },
			{ Entity: 'LineItem', Filter: `FBV~IDProject~EQ~${pIDProject}`, Destination: 'Bundle.LineItem', AllRecords: true, Projection: { Mode: 'LiteExtended', ExtraColumns: [ 'IDPayItem' ] } },
		];
		if (pMaterialSetID > 0)
		{
			// Narrowed to the pay items this project's line items actually name (PJU-chained off the
			// step above). The requirement rows the join can never reach are 41x the ones it can —
			// 61,480 set-wide against 1,487 for a large project on set 30 — and they are fetched on
			// every cold scope load, including the offline replay. The set filter stays alongside it:
			// a pay item can be named by a requirement row belonging to another set (set 32 carries
			// 318 rows against set 30's pay items), and only the set clause excludes those.
			tmpSteps.push(
				{
					Entity: 'PayItemMaterialTestRequirement',
					Filter: `FBV~IDTestSpecificationSet~EQ~${pMaterialSetID}~FBL~IDPayItem~INN~{~PJU:,^IDPayItem^Bundle.LineItem~}`,
					Destination: 'Bundle.PayItemMaterialTestRequirement',
					AllRecords: true,
					Projection: { Mode: 'LiteExtended', ExtraColumns: [ 'IDPayItem', 'SampleType' ] },
				});
		}
		if (this._needsMaterialHierarchy() && pIDCustomer > 0 && !this._primeMaterialHierarchyFromCache(pIDCustomer))
		{
			tmpSteps.push({ Entity: 'Material', Filter: `FBV~IDCustomer~EQ~${pIDCustomer}`, Destination: 'Bundle.Material', AllRecords: true, Projection: { Mode: 'LiteExtended', ExtraColumns: [ 'IDMaterialParent' ] } });
		}
		return tmpSteps;
	}

	/**
	 * Marshal executed-bundle data into the scope, applying the source choice locally:
	 * the project's own plan rows when it has any, else the template joined on IDPayItem.
	 *
	 * @param {Record<string, Array<any>>} pData - executed-bundle data keyed by entity.
	 * @param {number} pIDCustomer
	 */
	_applyScopeBundleData(pData, pIDCustomer)
	{
		if (Array.isArray(pData.Material) && pIDCustomer > 0)
		{
			const tmpPairs = [];
			for (const tmpRow of pData.Material)
			{
				const tmpID = parseInt(tmpRow.IDMaterial, 10) || 0;
				if (tmpID > 0)
				{
					tmpPairs.push([ tmpID, parseInt(tmpRow.IDMaterialParent, 10) || 0 ]);
				}
			}
			this._MaterialHierarchy = this._buildMaterialHierarchy(pIDCustomer, tmpPairs);
			if (this._resolveCacheConfig().Enabled)
			{
				this._writeCacheEntry(`${SPEC_SCOPE_CACHE_PREFIX}MaterialHierarchy:${pIDCustomer}`, { TS: Date.now(), Pairs: tmpPairs });
			}
		}
		const tmpPlanRows = pData.ProjectLineItemMaterialTest || [];
		if (tmpPlanRows.length > 0)
		{
			this._applyPlanEntries(this._planEntriesFromRows(tmpPlanRows), 'Plan');
			return;
		}
		this._applyPlanEntries(
			this._joinTemplateEntries(pData.LineItem || [], pData.PayItemMaterialTestRequirement || []),
			this._Scope.MaterialSetID > 0 ? 'Template' : 'None');
	}

	/**
	 * Join specification requirements to a project's line items on IDPayItem — the
	 * client-side half of the template source, for the bundle transport that has no
	 * server to do it. Mirrors the platform endpoint's join, including its exclusions:
	 * a line item with no pay item contributes nothing (and so does not constrain
	 * itself), and a requirement with a zero material or test id is a plan defect
	 * rather than a scope fact.
	 *
	 * @param {Array<Record<string, any>>} pLineItems
	 * @param {Array<Record<string, any>>} pRequirements
	 * @return {Array<PlanEntry>} distinct on (IDLineItem, IDMaterial, IDTest, SampleType).
	 */
	_joinTemplateEntries(pLineItems, pRequirements)
	{
		/** @type {Map<number, Array<number>>} */
		const tmpLineItemIDsByPayItem = new Map();
		for (const tmpRow of pLineItems)
		{
			const tmpIDLineItem = parseInt(tmpRow.IDLineItem, 10) || 0;
			const tmpIDPayItem = parseInt(tmpRow.IDPayItem, 10) || 0;
			if (!(tmpIDLineItem > 0) || !(tmpIDPayItem > 0))
			{
				continue;
			}
			if (!tmpLineItemIDsByPayItem.has(tmpIDPayItem))
			{
				tmpLineItemIDsByPayItem.set(tmpIDPayItem, []);
			}
			tmpLineItemIDsByPayItem.get(tmpIDPayItem).push(tmpIDLineItem);
		}
		const tmpSeen = new Set();
		const tmpEntries = [];
		for (const tmpRow of pRequirements)
		{
			const tmpIDMaterial = parseInt(tmpRow.IDMaterial, 10) || 0;
			const tmpIDTest = parseInt(tmpRow.IDTest, 10) || 0;
			if (!(tmpIDMaterial > 0) || !(tmpIDTest > 0))
			{
				continue;
			}
			const tmpSampleType = tmpRow.SampleType || '';
			for (const tmpIDLineItem of (tmpLineItemIDsByPayItem.get(parseInt(tmpRow.IDPayItem, 10) || 0) || []))
			{
				const tmpKey = `${tmpIDLineItem}|${tmpIDMaterial}|${tmpIDTest}|${tmpSampleType}`;
				if (tmpSeen.has(tmpKey))
				{
					continue;
				}
				tmpSeen.add(tmpKey);
				tmpEntries.push({ SampleType: tmpSampleType, IDLineItem: tmpIDLineItem, IDMaterial: tmpIDMaterial, IDTest: tmpIDTest });
			}
		}
		return tmpEntries;
	}

	/**
	 * Ancestor material IDs (walk IDMaterialParent up), excluding self. Empty when no hierarchy
	 * is loaded (fail-open — no expansion). Cycle-guarded.
	 *
	 * @param {number|string} pIDMaterial
	 * @return {Set<number>}
	 */
	getMaterialAncestorIDs(pIDMaterial)
	{
		const tmpResult = new Set();
		if (!this._MaterialHierarchy)
		{
			return tmpResult;
		}
		let tmpCurrent = parseInt(String(pIDMaterial), 10) || 0;
		let tmpGuard = 0;
		while (tmpCurrent > 0 && tmpGuard++ < 64)
		{
			const tmpParent = this._MaterialHierarchy.ParentOf.get(tmpCurrent) || 0;
			if (!(tmpParent > 0) || tmpResult.has(tmpParent))
			{
				break;
			}
			tmpResult.add(tmpParent);
			tmpCurrent = tmpParent;
		}
		return tmpResult;
	}

	/**
	 * Descendant material IDs (walk children down, transitive), excluding self. Empty when no
	 * hierarchy is loaded (fail-open). Cycle-guarded by the visited set.
	 *
	 * @param {number|string} pIDMaterial
	 * @return {Set<number>}
	 */
	getMaterialDescendantIDs(pIDMaterial)
	{
		const tmpResult = new Set();
		if (!this._MaterialHierarchy)
		{
			return tmpResult;
		}
		const tmpStack = [ parseInt(String(pIDMaterial), 10) || 0 ];
		while (tmpStack.length > 0)
		{
			const tmpChildren = this._MaterialHierarchy.ChildrenOf.get(tmpStack.pop());
			if (!tmpChildren)
			{
				continue;
			}
			for (const tmpChild of tmpChildren)
			{
				if (!tmpResult.has(tmpChild))
				{
					tmpResult.add(tmpChild);
					tmpStack.push(tmpChild);
				}
			}
		}
		return tmpResult;
	}

	/**
	 * Expand material anchors UP: the anchors plus all their ancestors. Used when a selected
	 * material is PUSHED as a filter to another input — the parent categories match too, so a
	 * leaf selection surfaces records bound to its category. Accepts a Set, array, comma-joined
	 * id string (a Multiple selector's value), or a single id; returns the union as a Set.
	 *
	 * @param {Iterable<number|string>|string|number} pIDs
	 * @return {Set<number>}
	 */
	expandMaterialAnchorsUp(pIDs)
	{
		const tmpResult = new Set();
		for (const tmpID of this._coerceMaterialIDList(pIDs))
		{
			tmpResult.add(tmpID);
			for (const tmpAncestor of this.getMaterialAncestorIDs(tmpID))
			{
				tmpResult.add(tmpAncestor);
			}
		}
		return tmpResult;
	}

	/**
	 * Expand an allowed material set DOWN: the set plus all descendants. Used when a dropdown
	 * filters its items by a material set — an item bound to one of the materials OR any child
	 * material is allowed. Accepts a Set, array, comma-joined id string, or a single id.
	 *
	 * @param {Iterable<number|string>|string|number} pIDs
	 * @return {Set<number>}
	 */
	expandMaterialSetDown(pIDs)
	{
		const tmpResult = new Set();
		for (const tmpID of this._coerceMaterialIDList(pIDs))
		{
			tmpResult.add(tmpID);
			for (const tmpDescendant of this.getMaterialDescendantIDs(tmpID))
			{
				tmpResult.add(tmpDescendant);
			}
		}
		return tmpResult;
	}

	/**
	 * Normalize a Set / array / comma-joined string / number into an array of positive ints.
	 *
	 * @param {Iterable<number|string>|string|number} pIDs
	 * @return {number[]}
	 */
	_coerceMaterialIDList(pIDs)
	{
		if (pIDs === null || pIDs === undefined)
		{
			return [];
		}
		let tmpRaw;
		if (typeof pIDs === 'string')
		{
			tmpRaw = pIDs.split(',');
		}
		else if (typeof pIDs === 'number')
		{
			tmpRaw = [ pIDs ];
		}
		else
		{
			tmpRaw = Array.from(pIDs);
		}
		const tmpResult = [];
		for (const tmpValue of tmpRaw)
		{
			const tmpID = parseInt(String(tmpValue).trim(), 10) || 0;
			if (tmpID > 0)
			{
				tmpResult.push(tmpID);
			}
		}
		return tmpResult;
	}

	/**
	 * Whether any descriptor on the loaded form carries a DynamicFilters entry matching
	 * a predicate. Returns false when no manifest is available.
	 *
	 * @param {(pFilter: Record<string, any>) => boolean} fPredicate
	 * @return {boolean}
	 */
	_hasDynamicFilter(fPredicate)
	{
		const tmpDescriptors = this.pict?.views?.PictFormMetacontroller?.manifestDescription?.Descriptors;
		if (!tmpDescriptors)
		{
			return false;
		}
		for (const tmpDescriptor of Object.values(tmpDescriptors))
		{
			const tmpFilters = tmpDescriptor?.PictForm?.DynamicFilters;
			if (Array.isArray(tmpFilters) && tmpFilters.some((pFilter) => !!pFilter && fPredicate(pFilter)))
			{
				return true;
			}
		}
		return false;
	}

	/**
	 * Whether the loaded form has a plan-scoped selector — a descriptor with a
	 * SamplePlanScope entry in its PictForm.DynamicFilters. Gates the plan-entry reads so
	 * forms that don't use them pay nothing.
	 *
	 * @return {boolean}
	 */
	_needsSamplePlan()
	{
		return this._hasDynamicFilter((pFilter) => !!pFilter.SamplePlanScope);
	}

	/**
	 * Whether any filter on the form expands a material anchor through the material tree
	 * — the plan scopes, and a Through hop keyed on IDMaterial.
	 *
	 * @return {boolean}
	 */
	_needsMaterialHierarchy()
	{
		return this._needsSamplePlan() || this._hasDynamicFilter((pFilter) => !!pFilter.Through && pFilter.ThroughKey === 'IDMaterial');
	}

	/**
	 * Resolve the project's plan entries from the per-entity reads. The project's own
	 * ProjectLineItemMaterialTest rows win when it has any; otherwise the platform
	 * derives the template from the contract's specification set. Uses the
	 * LiteExtended/SampleType projection — Lite carries the IDs but not the SampleType
	 * Hash; LiteExtended/SampleType adds it while staying offline-servable.
	 *
	 * @param {number} pIDProject
	 * @return {Promise<{ Combos: Array<PlanEntry>, Source: PlanEntrySource }>}
	 */
	async _loadSamplePlan(pIDProject)
	{
		const tmpRows = await this._readAllPages('ProjectLineItemMaterialTests/LiteExtended/SampleType', `FBV~IDProject~EQ~${pIDProject}`);
		if (tmpRows.length > 0)
		{
			return { Combos: this._planEntriesFromRows(tmpRows), Source: 'Plan' };
		}
		return this._loadTemplateEntries(pIDProject);
	}

	/**
	 * Ask the platform for the specification template governing a project with no plan.
	 * Fails open on a missing or erroring endpoint — a client running ahead of the
	 * platform must leave its pickers unnarrowed, not blank them.
	 *
	 * @param {number} pIDProject
	 * @return {Promise<{ Combos: Array<PlanEntry>, Source: PlanEntrySource }>}
	 */
	async _loadTemplateEntries(pIDProject)
	{
		try
		{
			const tmpResponse = await this._getJSON(`TestSpecificationSet/PlanEntries/ForProject/${pIDProject}`);
			return {
				Combos: this._planEntriesFromRows(tmpResponse?.Entries || []),
				Source: tmpResponse?.Source || 'None',
			};
		}
		catch (pError)
		{
			this.pict?.log?.warn(`[SpecScopeResolver] Plan-entry template load failed for project ${pIDProject} (fail-open): ${pError?.message || pError}`);
			return { Combos: [], Source: 'None' };
		}
	}

	/**
	 * Normalize rows from any transport into plan entries.
	 *
	 * @param {Array<any>} pRows
	 * @param {(pRow: any) => Record<string, any>} [fPick] - maps a row onto the four fields.
	 * @return {Array<PlanEntry>}
	 */
	_planEntriesFromRows(pRows, fPick)
	{
		return pRows.map((pRow) =>
		{
			const tmpRow = fPick ? fPick(pRow) : pRow;
			return {
				SampleType: tmpRow.SampleType || '',
				IDLineItem: parseInt(tmpRow.IDLineItem, 10) || 0,
				IDMaterial: parseInt(tmpRow.IDMaterial, 10) || 0,
				IDTest: parseInt(tmpRow.IDTest, 10) || 0,
			};
		});
	}

	/**
	 * Resolve the allowed IDs for one plan axis (pField), narrowed by whatever
	 * upstream keys are supplied in pKeys. An absent / empty / zero key does not
	 * narrow on that axis (so the top of the cascade returns the whole plan set).
	 * Returns null when no plan is loaded — the fail-open signal.
	 *
	 * @param {('IDLineItem'|'IDMaterial'|'IDTest')} pField
	 * @param {{SampleType?:string, IDLineItem?:number, IDMaterial?:number, IDTest?:number}} pKeys
	 * @return {Set<number>|null}
	 */
	_samplePlanIDs(pField, pKeys)
	{
		if (!this._Scope.HasSamplePlan || !Array.isArray(this._Scope.SamplePlanCombos))
		{
			return null;
		}
		// Normalize each provided key to a Set of accepted values. A Multiple selector
		// (e.g. the LineItem picker) hands a comma-joined list like "12,34", so a key may
		// accept several values (union/IN). An absent / empty / zero key does not narrow.
		const tmpKeySets = {};
		for (const tmpKey of Object.keys(pKeys))
		{
			const tmpRaw = pKeys[tmpKey];
			if (tmpRaw === null || tmpRaw === undefined || tmpRaw === '' || tmpRaw === 0)
			{
				continue;
			}
			const tmpValues = String(tmpRaw).split(',').map((pValue) => pValue.trim()).filter((pValue) => pValue !== '' && pValue !== '0');
			if (tmpValues.length === 0)
			{
				continue;
			}
			if (tmpKey === 'SampleType')
			{
				tmpKeySets[tmpKey] = new Set(tmpValues);
				continue;
			}
			// Numeric axes (IDLineItem / IDMaterial / IDTest). A non-numeric anchor — e.g. a hash-keyed Test
			// selector handing "D5249v1" — would parseInt to NaN and match no plan combo, wrongly constraining the
			// dependent dropdown to none. Drop non-numeric values; if none remain, this axis simply does not narrow
			// (the same fail-open as an absent / empty / zero key) rather than blanking the dropdown.
			const tmpNumeric = tmpValues.map((pValue) => parseInt(pValue, 10)).filter((pValue) => !Number.isNaN(pValue));
			if (tmpNumeric.length === 0)
			{
				continue;
			}
			tmpKeySets[tmpKey] = new Set(tmpNumeric);
		}
		const tmpResult = new Set();
		for (const tmpCombo of this._Scope.SamplePlanCombos)
		{
			let tmpMatch = true;
			for (const tmpKey of Object.keys(tmpKeySets))
			{
				const tmpComboValue = (tmpKey === 'SampleType') ? String(tmpCombo.SampleType) : tmpCombo[tmpKey];
				if (!tmpKeySets[tmpKey].has(tmpComboValue))
				{
					tmpMatch = false;
					break;
				}
			}
			if (tmpMatch && tmpCombo[pField] > 0)
			{
				tmpResult.add(tmpCombo[pField]);
			}
		}
		return tmpResult;
	}

	/**
	 * Allowed LineItem IDs from the sampling plan, narrowed by SampleType and/or
	 * the selected Material / Test (bidirectional plan cross-filter). Null when no
	 * plan (fail-open).
	 *
	 * @param {string} [pSampleType]
	 * @param {number} [pIDMaterial]
	 * @param {number} [pIDTest]
	 * @return {Set<number>|null}
	 */
	getSamplePlanLineItemIDs(pSampleType, pIDMaterial, pIDTest)
	{
		return this._samplePlanIDs('IDLineItem', { SampleType: pSampleType, IDMaterial: pIDMaterial, IDTest: pIDTest });
	}

	/**
	 * Allowed Material IDs from the sampling plan, narrowed by SampleType and/or
	 * the selected LineItem / Test (bidirectional plan cross-filter). Null when no
	 * plan (fail-open).
	 *
	 * @param {string} [pSampleType]
	 * @param {number} [pIDLineItem]
	 * @param {number} [pIDTest]
	 * @return {Set<number>|null}
	 */
	getSamplePlanMaterialIDs(pSampleType, pIDLineItem, pIDTest)
	{
		return this._samplePlanIDs('IDMaterial', { SampleType: pSampleType, IDLineItem: pIDLineItem, IDTest: pIDTest });
	}

	/**
	 * Allowed Test IDs from the sampling plan, narrowed by SampleType, LineItem,
	 * and/or Material. Null when no plan (fail-open).
	 *
	 * @param {string} [pSampleType]
	 * @param {number} [pIDLineItem]
	 * @param {number} [pIDMaterial]
	 * @return {Set<number>|null}
	 */
	getSamplePlanTestIDs(pSampleType, pIDLineItem, pIDMaterial)
	{
		return this._samplePlanIDs('IDTest', { SampleType: pSampleType, IDLineItem: pIDLineItem, IDMaterial: pIDMaterial });
	}

	/**
	 * Resolve a contract's specification-set reference. Two-tiered: the direct
	 * IDTestSpecificationSet FK wins; when it's unset, fall back to matching a
	 * TestSpecificationSet by the contract's spec Year. Returns 0 when neither
	 * referent is set (the template source then does not apply).
	 *
	 * @param {Record<string, any>} pContract
	 * @param {string} pIDField - direct set-ID field, e.g. 'MaterialIDTestSpecificationSet'.
	 * @param {string} pYearField - specification-year field, e.g. 'MaterialsSpecificationYear'.
	 * @return {Promise<number>}
	 */
	async _resolveSetID(pContract, pIDField, pYearField)
	{
		const tmpDirectID = parseInt(pContract?.[pIDField], 10) || 0;
		if (tmpDirectID > 0)
		{
			return tmpDirectID;
		}
		const tmpYearValue = pContract?.[pYearField];
		if (tmpYearValue == null)
		{
			return 0;
		}
		const tmpYear = String(tmpYearValue).trim();
		if (tmpYear === '' || tmpYear === '0')
		{
			return 0;
		}
		// A year is NOT unique: a customer may run a provisional set alongside the live one for the same year.
		// Nothing on TestSpecificationSet distinguishes them, so take the lowest id — the earliest set for the
		// year — and say so when the year is ambiguous rather than resolving it silently.
		const tmpSets = await this._readAllPages('TestSpecificationSets/LiteExtended/Year', `FBV~Year~EQ~${encodeURIComponent(tmpYear)}~FSF~IDTestSpecificationSet~ASC~0`);
		if (tmpSets.length > 1)
		{
			this.pict?.log?.warn(`[SpecScopeResolver] Specification year ${tmpYear} matches ${tmpSets.length} specification sets; using the lowest id (${tmpSets[0]?.IDTestSpecificationSet}). Set the contract's ${pIDField} to resolve this explicitly.`);
		}
		return parseInt(tmpSets[0]?.IDTestSpecificationSet, 10) || 0;
	}

	/**
	 * Page through a filtered meadow read until exhausted. Plain FilteredTo
	 * reads (no Count/aggregate route) so the offline client can serve them
	 * from synced entity data.
	 *
	 * @param {string} pEntityRoute - pluralized route incl. projection, e.g. 'PayItems/Lite'.
	 * @param {string} pFilter - meadow filter stanza, e.g. 'FBV~IDTestSpecificationSet~EQ~12'.
	 * @return {Promise<Array<any>>}
	 */
	async _readAllPages(pEntityRoute, pFilter)
	{
		const tmpRows = [];
		let tmpOffset = 0;
		for (;;)
		{
			const tmpPage = await this._getJSON(`${pEntityRoute}/FilteredTo/${pFilter}/${tmpOffset}/${SPEC_SCOPE_PAGE_SIZE}`);
			if (!Array.isArray(tmpPage) || tmpPage.length === 0)
			{
				break;
			}
			for (const tmpRow of tmpPage)
			{
				tmpRows.push(tmpRow);
			}
			if (tmpPage.length < SPEC_SCOPE_PAGE_SIZE)
			{
				break;
			}
			tmpOffset += SPEC_SCOPE_PAGE_SIZE;
		}
		return tmpRows;
	}

	/**
	 * @param {Array<any>} pValues
	 * @return {Array<number>} distinct positive integers.
	 */
	_distinctPositive(pValues)
	{
		const tmpSet = new Set();
		for (const tmpValue of pValues)
		{
			const tmpNumber = parseInt(tmpValue, 10);
			if (tmpNumber > 0)
			{
				tmpSet.add(tmpNumber);
			}
		}
		return Array.from(tmpSet);
	}

	_getJSON(pUrl)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(pUrl, (err, body) =>
			{
				if (err)
				{
					return reject(err);
				}
				resolve(body);
			});
		});
	}

	/**
	 * @return {Set<number>|null} Allowed lab IDs — labs joined to the material spec set via
	 * LabTestSpecificationSetJoin — when the set has lab-join coverage, else null (no constraint).
	 */
	getAllowedLabIDs()
	{
		return this._Scope.HasLabCoverage ? this._Scope.AllowedLabIDs : null;
	}

	/**
	 * Floor the lab axis to labs joined to the resolved material spec set (LabTestSpecificationSetJoin). Fails open:
	 * on no set, empty coverage, or a read error, getAllowedLabIDs returns null and the lab picker is unconstrained.
	 * @return {Promise<void>}
	 */
	async _loadLabScope()
	{
		const tmpSetID = this._Scope.MaterialSetID;
		if (!(tmpSetID > 0))
		{
			return;
		}
		try
		{
			const tmpRows = await this._readAllPages('LabTestSpecificationSetJoins/Lite', `FBV~IDTestSpecificationSet~EQ~${tmpSetID}`);
			this._Scope.AllowedLabIDs = new Set(this._distinctPositive(tmpRows.map((pRow) => pRow.IDLab)));
			this._Scope.HasLabCoverage = this._Scope.AllowedLabIDs.size > 0;
		}
		catch (pError)
		{
			this.pict?.log?.warn(`[SpecScopeResolver] Lab scope load failed (fail-open): ${pError?.message || pError}`);
		}
	}

	/**
	 * @return {PlanEntrySource} which source answered for the loaded project. No picker
	 * reads this; it is what answers "why can't I pick this".
	 */
	getSource()
	{
		return this._Scope.Source;
	}

	/**
	 * @return {boolean} True when resolver has completed its load attempt.
	 */
	isLoaded()
	{
		return this._Scope.Loaded;
	}

	/**
	 * App-load hook — auto-prime scope from whatever sample/project the
	 * loaded form points at. Fires automatically because PictProvider
	 * defaults `AutoLoadDataWithApp: true`. We don't override that flag,
	 * so we participate in the load cycle without host-app changes.
	 *
	 * Resolution strategy (mirrors Materials-Form-Manager.js:605-626 for
	 * the manifest walk, then falls back to legacy + document-level addresses):
	 *
	 *   1. Walk manifest descriptors for a sample-bearing InputType
	 *      (SampleSelector / MaterialsSampleSelector / MaterialsHiddenSampleSelector,
	 *      plus EntitySelector / EntitySelectorMultiple with Entity='Sample').
	 *   2. First match wins. If a __Record companion exists at
	 *      `<DataAddress>__Record`, forward via loadForSampleRecord (no
	 *      extra REST). Else read the bare value and forward via
	 *      loadForSampleByIdentifier with the descriptor's IdentityField.
	 *   3. Hard-coded legacy fallback: `SampleID__Record`.
	 *   4. Final fallback: `ReportData.IDProject` → loadForProject.
	 *   5. No suitable signal: silently skip (host apps that already call
	 *      loadForProject explicitly continue to work; resolver dedupes).
	 *
	 * Failure here is non-fatal — the resolver fails open and pickers
	 * see the full catalog.
	 *
	 * @param {(error?: Error) => void} fCallback
	 */
	onAfterLoadDataAsync(fCallback)
	{
		return super.onAfterLoadDataAsync((pError) =>
		{
			if (pError)
			{
				return fCallback(pError);
			}
			this._resolveSpecScopeFromForm().catch((err) =>
			{
				this.pict?.log?.warn(`[SpecScopeResolver] Auto-resolve on app load failed: ${err?.message || err}`);
			}).finally(() =>
			{
				fCallback();
			});
		});
	}

	/**
	 * @return {Promise<void>}
	 */
	async _resolveSpecScopeFromForm()
	{
		const tmpReportData = this.pict.AppData?.DocumentData?.ReportData;
		const tmpFormData = tmpReportData?.FormData;
		const tmpInfo = this._findSampleDescriptorInfo();
		if (tmpInfo && tmpFormData)
		{
			const tmpRecord = this.pict.manifest.getValueAtAddress(tmpFormData, `${tmpInfo.dataAddress}__Record`);
			if (tmpRecord && tmpRecord.IDProject > 0)
			{
				await this.loadForSampleRecord(tmpRecord);
				return;
			}
			const tmpValue = this.pict.manifest.getValueAtAddress(tmpFormData, tmpInfo.dataAddress);
			if (tmpValue)
			{
				await this.loadForSampleByIdentifier(tmpValue, tmpInfo.identityField);
				return;
			}
		}
		// Legacy fallback. Older forms without a discoverable sample-selector
		// descriptor sometimes still maintain a SampleID__Record companion.
		const tmpLegacyRecord = tmpFormData ? this.pict.manifest.getValueAtAddress(tmpFormData, 'SampleID__Record') : null;
		if (tmpLegacyRecord && tmpLegacyRecord.IDProject > 0)
		{
			await this.loadForSampleRecord(tmpLegacyRecord);
			return;
		}
		// Final fallback: document-level project. Forms with no sample concept
		// at all (e.g. document-level reports) still get scope from this path.
		const tmpDocProjectID = parseInt(tmpReportData?.IDProject, 10) || 0;
		if (tmpDocProjectID > 0)
		{
			await this.loadForProject(tmpDocProjectID);
		}
	}

	/**
	 * @return {{ dataAddress: string, identityField: string }|null}
	 */
	_findSampleDescriptorInfo()
	{
		const tmpManifest = this.pict.views?.PictFormMetacontroller?.manifestDescription;
		if (!tmpManifest || !tmpManifest.Descriptors)
		{
			return null;
		}
		const SAMPLE_INPUT_TYPES = new Set(
			[ 'SampleSelector', 'MaterialsSampleSelector', 'MaterialsHiddenSampleSelector' ]);
		// Materials* selectors store GUIDSample by default (see
		// _getDefaultRecordIdentity in PictProvider-InputType-MaterialsSampleSelector.js
		// line 28). SampleSelector and EntitySelector default to IDSample.
		const MATERIALS_SAMPLE_SELECTORS = new Set(
			[ 'MaterialsSampleSelector', 'MaterialsHiddenSampleSelector' ]);
		for (const [ tmpKey, tmpDescriptor ] of Object.entries(tmpManifest.Descriptors))
		{
			const tmpInputType = tmpDescriptor.PictForm?.InputType;
			const tmpEntity = tmpDescriptor.PictForm?.Entity;
			const tmpIsExplicit = SAMPLE_INPUT_TYPES.has(tmpInputType);
			const tmpIsGeneric = (tmpInputType === 'EntitySelector' || tmpInputType === 'EntitySelectorMultiple') && tmpEntity === 'Sample';
			if (tmpIsExplicit || tmpIsGeneric)
			{
				let tmpIdentityField = tmpDescriptor.PictForm?.IdentityField;
				if (!tmpIdentityField)
				{
					tmpIdentityField = MATERIALS_SAMPLE_SELECTORS.has(tmpInputType) ? 'GUIDSample' : 'IDSample';
				}
				return {
					dataAddress: tmpDescriptor.DataAddress || tmpKey,
					identityField: tmpIdentityField,
				};
			}
		}
		return null;
	}

	/**
	 * Convenience for callers that already have a Sample record in hand
	 * (e.g. MaterialsSampleSelector after _onRecordRefresh). Pulls IDProject
	 * off the record and forwards to loadForProject. No-op when the record
	 * is missing or doesn't carry a populated IDProject.
	 *
	 * @param {Record<string, any>} pSampleRecord
	 * @return {Promise<object>} the scope snapshot
	 */
	loadForSampleRecord(pSampleRecord)
	{
		const tmpIDProject = parseInt(pSampleRecord?.IDProject, 10) || 0;
		if (!(tmpIDProject > 0))
		{
			return Promise.resolve(this._Scope);
		}
		return this.loadForProject(tmpIDProject);
	}

	/**
	 * Convenience for callers that have a Sample identifier (typically
	 * IDSample, sometimes GUIDSample for not-yet-saved samples). Fetches
	 * the matching Sample row by the named identity field and forwards
	 * to loadForSampleRecord.
	 *
	 * Selectors that store IDSample (SampleSelector, EntitySelector with
	 * default IdentityField) call with `pIdentityField='IDSample'`.
	 * MaterialsSampleSelector stores GUIDSample but it has the record in
	 * hand, so it uses loadForSampleRecord directly. EntitySelector with a
	 * non-default IdentityField passes that through.
	 *
	 * @param {string|number} pSampleIdentifier
	 * @param {string} [pIdentityField='IDSample']
	 * @return {Promise<object>} the scope snapshot
	 */
	async loadForSampleByIdentifier(pSampleIdentifier, pIdentityField = 'IDSample')
	{
		if (pSampleIdentifier === null || pSampleIdentifier === undefined || pSampleIdentifier === '' || pSampleIdentifier === 'null')
		{
			return this._Scope;
		}
		try
		{
			const tmpRows = await this._getJSON(`Samples/FilteredTo/FBV~${pIdentityField}~EQ~${encodeURIComponent(pSampleIdentifier)}`);
			const tmpRecord = Array.isArray(tmpRows) ? tmpRows[0] : tmpRows;
			return this.loadForSampleRecord(tmpRecord);
		}
		catch (err)
		{
			this.pict?.log?.warn(`[SpecScopeResolver] Failed to fetch Sample (${pIdentityField}=${pSampleIdentifier}): ${err?.message || err}`);
			return this._Scope;
		}
	}
}

module.exports = SpecScopeResolver;
